<template>
  <div class="vz-individual-order-cargo-wrapping" :class="{'vz-individual-order-cargo-wrapping-simple': simple}">
    <div v-if="hasTitle" class="text-subtitle color-dark vz-individual-order-cargo-wrapping-title">
      Упаковка
    </div>
    <div v-if="simple" class="mb-20">
      <vz-light-order-wrapping @save="updateWrappingInStore" />
    </div>
    <vz-wrapping-review
      :class="wrappingNotEmptyClass"
      :wrapping="wrapping"
      :disabled="disabled"
      @remove-item="removeItem"
      @remove-place="removePlace"
      @add-place="addPlace"
      @show-modal="showModal"
    />
    <vz-button
      v-if="!simple"
      icon-name="plus"
      variant="primary"
      outline
      big
      class="add-card-button"
      :class="wrappingNotEmptyClass"
      :disabled="wrappingDisabled"
      @click="showModal"
    >
      Добавить
    </vz-button>
    <vz-individual-order-wrapping-modal
      ref="orderWrappingModal"
      @save="updateWrappingInStore"
    />
    <vz-card v-if="hasPartialWrapping" variant="warning" class="active p-10 mt-20">
      После оформления заявки рекомендуем Вам распечатать "<b>Маркировочный лист</b>",
      отметить в нём какие места необходимо упаковать и прикрепить листы к соответствующим местам.
      Это позволит при приёмке груза упаковать именно то, что Вам необходимо.
    </vz-card>
  </div>
</template>

<script setup lang="ts">
import {OrderWrappingItem, VzClass} from '~/types/types';
import {useOrderStore} from '~/stores/order';
import {deepCopyObject} from '~/common/functions';
import VzIndividualOrderWrappingModal from '~/components/modals/vz-individual-order-wrapping-modal.vue';
import VzWrappingReview from '~/components/personal/order/cargo/parts/vz-wrapping-review.vue';
import VzButton from '~/components/ui-kit/vz-button.vue';
import VzLightOrderWrapping from '~/components/personal/order/light-order/vz-light-order-wrapping.vue';
import VzCard from '~/components/ui-kit/vz-card.vue';

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false
  },
  simple: {
    type: Boolean,
    default: false
  },
})

const orderStore = useOrderStore()

const orderWrappingModal = ref()

const mode = computed(() => {
  return orderStore.orderForm.cargo.mode
})

const wrappingDisabled = computed(() => {
  return !orderStore.orderForm.cargo.type.cargo || props.disabled
})

const wrapping = computed((): OrderWrappingItem[] => {
  let wrapping: OrderWrappingItem[] = []
  if (mode.value === 'dimension') {
    wrapping = orderStore.orderForm.cargo.dimensions.wrapping
  } else if (mode.value === 'pallets' || mode.value === 'places') {
    let itemsWrapping: OrderWrappingItem[] = []
    orderStore.orderForm.cargo[mode.value].items.forEach((item) => {
      itemsWrapping = [...itemsWrapping, ...item.wrapping]
    })
    itemsWrapping.forEach((item) => {
      if (!wrapping.find(dataItem => dataItem.code === item.code)) {
        wrapping.push(item)
      }
    })
  }
  return sortWrapping(wrapping)
})

const hasTitle = computed(() => {
  return wrapping.value.length === 0 || props.simple
})

const hasPartialWrapping = computed(() => {
  // @ts-ignore
  const data = deepCopyObject(orderStore.orderForm.cargo[orderStore.orderForm.cargo.mode]?.items)
  if (!data || data?.length <= 1) {
    return false
  }
  const first = new Set(data[0]?.wrapping.map((item: any) => item.code))
  // Нашли хотя бы одну упаковку не на все места
  const find = data.find((item: any) => {
    const itemSet = (new Set(item?.wrapping.map((w: any) => w.code)))
    return itemSet.size !== first.size || !([...itemSet].every((x) => first.has(x)))
  })
  return Boolean(first && find)
})

const wrappingNotEmptyClass = computed((): VzClass => {
  return {
    'wrapping-not-empty': !hasTitle.value,
    'color-inactive': props.disabled
  }
})

function sortWrapping(data: OrderWrappingItem[]): OrderWrappingItem[] {
  return data.sort((a, b) => a.code > b.code ? 1 : -1)
}

function showModal() {
  orderWrappingModal.value.show()
}

function hideModal() {
  orderWrappingModal.value.hide()
}

function updateWrappingInStore(value: OrderWrappingItem[]) {
  const oldWrapping = deepCopyObject(wrapping.value)
  if (mode.value === 'dimension') {
    orderStore.orderForm.cargo.dimensions.wrapping = deepCopyObject(value) as OrderWrappingItem[]
  } else {
    orderStore.orderForm.cargo[mode.value].items.forEach((item: any) => {
      let newItemWrapping = deepCopyObject(value)
      value.forEach((i) => {
        if (oldWrapping.find(oldItem => oldItem.code === i.code) && !item.wrapping.find((el: any) => el.code === i.code)) {
          newItemWrapping = newItemWrapping.filter(el => el.code !== i.code)
        }
      })
      item.wrapping = deepCopyObject(newItemWrapping) as OrderWrappingItem[]
    })
  }
  hideModal()
  orderStore.getOrderPriceAction()
}

function removeItem(code: string) {
  const newWrapping = wrapping.value.filter((item) => { return item.code !== code })
  updateWrappingInStore(newWrapping)
}

function removePlace(data: { code: string, forAll: boolean, index: number }) {
  if (mode.value !== 'dimension' && mode.value !== 'parcels') {
    if (data.forAll) {
      const newItems = orderStore.orderForm.cargo[mode.value].items
      newItems.forEach((item, index) => {
        item.wrapping = item.wrapping.filter((wrapping) => {
          return wrapping.code !== data.code || (wrapping.code === data.code && index === 0)
        })
      })
      orderStore.orderForm.cargo[mode.value].items = deepCopyObject(newItems)
    } else {
      const newItemWrapping = orderStore.orderForm.cargo[mode.value].items[data.index].wrapping.filter((item) => { return item.code !== data.code })
      orderStore.orderForm.cargo[mode.value].items[data.index].wrapping = deepCopyObject(newItemWrapping)
    }
  }
}

function addPlace(data: { code: string, forAll: boolean, index: number }) {
  if (mode.value !== 'dimension' && mode.value !== 'parcels') {
    if (data.forAll) {
      const newItems = orderStore.orderForm.cargo[mode.value].items
      newItems.forEach((item) => {
        if (!item.wrapping.find(i => i.code === data.code)) {
          item.wrapping.push({ code: data.code, value: 1 })
        }
      })
      orderStore.orderForm.cargo[mode.value].items = deepCopyObject(newItems)
    } else {
      orderStore.orderForm.cargo[mode.value].items[data.index].wrapping.push({ code: data.code, value: 0 })
    }
  }
}
</script>
